import {
  audioFrameFromProclaimSnapshot,
  bindAudioSettingsPanel,
  createAudioEngine,
  PRELOAD_COMBAT_IDS,
  PRELOAD_MUSIC_IDS,
  PRELOAD_STATE_IDS,
  type AudioEngine,
  type AudioSettingsPanelHandle,
} from "../../shared/audio";
import "../../shared/cards/card-face.css";
import { cardFullscreen } from "../../shared/cards/CardFullscreen";
import { preloadLieArt } from "../../shared/cards/lieArt";
import { preloadPrayerArt } from "../../shared/cards/prayerArt";
import {
  GALLERY_CHAPTER_PIECES,
  copiesOwned,
  grantCards,
  loadCollection,
  ownedVerseIds,
} from "../../shared/collection";
import {
  getActiveDeckId,
  getDeck,
  listDecks,
  loadDeck,
  markDeckUsed,
  resolvePlayableDeckById,
  setActiveDeck,
} from "../../shared/deck/storage";
import {
  BOT_LABELS,
  createBotController,
  type BotController,
  type BotId,
} from "./bot/BotRunner";
import {
  applyRewardPick,
  CAMPAIGN_MISSIONS,
  campaignForMissionId,
  finalizeVictory,
  isDifficultyCleared,
  isMissionUnlocked,
  loadCampaign,
  missionById,
  processVictory,
  resolveMissionLoadout,
  scaleScenario,
  type ActiveRun,
  type CampaignDifficulty,
  type CampaignSave,
  type MissionDef,
  type RewardOutcome,
} from "./campaign";
import { campaignLieArtIds } from "./campaign/lieSkins";
import { ENDLESS_FREE_PLAY, MUSTER_FREE_PLAY } from "./data/endless";
import { LIES } from "./data/lies";
import {
  VERSES,
  WISDOM_GIFT_ID,
  WISDOM_VERSE_IDS,
  verseById,
} from "./data/verses";
import type { Category, Difficulty } from "./game/board/types";
import { isDogmaPeelAnswer } from "./game/combat/dogma";
import { dragAimMode } from "./game/cards/dragAim";
import { planLegalTargets } from "./game/cards/legalTargets";
import {
  claimEndlessArtPack,
  claimEndlessPackCard,
  evaluateEndlessPack,
  PACK_PITY_SURVIVE_SECONDS,
  PACK_SURVIVE_SECONDS,
  packReasonLead,
  skipEndlessPack,
} from "./game/endlessPacks";
import {
  formatCheckpointSummary,
  getEndlessCheckpoint,
  getResumeCheckpoint,
  loadoutKey,
  saveEndlessCheckpoint,
} from "./game/endlessCheckpoint";
import {
  formatSurviveTime,
  recordEndlessScore,
} from "./game/endlessScore";
import {
  buildMusterCullOptions,
  buildMusterFindingOptions,
  canInscribeToMuster,
  canRemoveFromMuster,
  getMusterDeck,
  inscribeToMuster,
  MUSTER_MAX,
  musterEndLead,
  recordMusterScore,
  removeFromMuster,
  uniqueFindings,
} from "./game/muster";
import { Game } from "./game/Game";
import { createVerseCardFace } from "../../shared/cards/cardFace";
import { BoardUi } from "./ui/BoardUi";
import { CampaignHub } from "./ui/CampaignHub";
import { bindCombatFeedbackPanel } from "./ui/CombatFeedbackPanel";
import {
  CoachUi,
  loadCoachProgress,
  markCoachStep,
  ObjectiveBanner,
  type CoachProgress,
} from "./ui/Coach";
import { DiscoursePopups } from "./ui/DiscoursePopups";
import { EndScreen } from "./ui/EndScreen";
import { HandUi, isConstrainedPlayShell } from "./ui/HandUi";
import { Hud } from "./ui/Hud";
import { LieInspect } from "./ui/LieInspect";
import { RevealModal } from "./ui/RevealModal";
import { StoryPanels } from "./ui/StoryPanels";
import { RewardPicker } from "./ui/RewardPicker";

// Warm presence art so first spawn does not flash monogram
preloadLieArt([...LIES.map((l) => l.id), ...campaignLieArtIds()]);
preloadPrayerArt(
  VERSES.filter(
    (v) => v.kind === "placeable" || v.convertPrayerName,
  ).map((v) => v.id),
);

const boardRoot = document.getElementById("board");
const handRoot = document.getElementById("hand");
if (!boardRoot || !handRoot) throw new Error("Missing #board or #hand");

function parsePlayMode(): "campaign" | "free" {
  const q = new URLSearchParams(window.location.search).get("mode");
  if (q === "free" || q === "muster") return "free";
  if (q === "campaign") return "campaign";
  // Default: campaign shell (free play available from hub / ?mode=free)
  return "campaign";
}

function parseFreeVariant(): "open" | "muster" {
  const q = new URLSearchParams(window.location.search).get("mode");
  return q === "muster" ? "muster" : "open";
}

/** Hub deep-link: skip the checkpoint chooser when the player already picked. */
function parseFreePlayStart(): "resume" | "new" | null {
  const q = new URLSearchParams(window.location.search).get("start");
  if (q === "resume" || q === "new") return q;
  return null;
}

/** Deep-link into hub: library (no open campaign) or stage map. */
function parseHubBootOpts(): {
  view: "library" | "missions";
  campaignId?: string;
  selectMissionId?: string;
} {
  const q = new URLSearchParams(window.location.search);
  const view = q.get("view") === "missions" ? "missions" : "library";
  const campaignId = q.get("campaign") || undefined;
  const selectMissionId = q.get("mission") || undefined;
  if (view === "missions") {
    return { view, campaignId, selectMissionId };
  }
  return { view: "library" };
}

let playMode: "campaign" | "free" = parsePlayMode();
/** Open endless (your deck) vs Muster climb (persistent thin start deck). */
let freeVariant: "open" | "muster" = parseFreeVariant();
let campaignSave: CampaignSave = loadCampaign();
let activeRun: ActiveRun | null = null;
/** Muster mid-run provision UI is open (game paused). */
let musterFindingOpen = false;
/** Pending end-screen after reward UI closes. */
let pendingEndReport: {
  won: boolean;
  lastMessage: string;
  stats: ReturnType<Game["getRunReport"]>["stats"];
  log: ReturnType<Game["getRunReport"]>["log"];
} | null = null;

const game = new Game();
const hud = new Hud();
const discourse = new DiscoursePopups();
const combatFeedbackPanel = bindCombatFeedbackPanel(document);
// Default discourse Off — board FX own the fight; user can raise in Settings
discourse.setLevel(combatFeedbackPanel.getSettings().discourse);
const lieInspect = new LieInspect();

const campaignOverlay = document.getElementById("campaign-overlay");
const storyOverlay = document.getElementById("story-overlay");
const rewardOverlay = document.getElementById("reward-overlay");
const btnCampaign = document.getElementById(
  "btn-campaign",
) as HTMLButtonElement | null;
const difficultyWrap = document.getElementById("difficulty-wrap");
const pauseBanner = document.getElementById("pause-banner");
const coachOverlay = document.getElementById("coach-overlay");
const secretPlaceNote = document.getElementById("secret-place-note");
const objectiveBannerRoot = document.getElementById("objective-banner");

const coach = coachOverlay ? new CoachUi(coachOverlay) : null;
const objectiveBanner = objectiveBannerRoot
  ? new ObjectiveBanner(objectiveBannerRoot)
  : null;

/** X / iframe shells steal card drags — nudge players to open freestanding. */
const CONSTRAINED_SHELL_DISMISS_KEY = "sots-constrained-shell-dismissed";
(function initConstrainedShellBanner(): void {
  const banner = document.getElementById("constrained-shell-banner");
  if (!banner || !isConstrainedPlayShell()) return;
  try {
    if (sessionStorage.getItem(CONSTRAINED_SHELL_DISMISS_KEY) === "1") return;
  } catch {
    /* ignore */
  }
  banner.classList.remove("hidden");
  const open = document.getElementById(
    "constrained-shell-open",
  ) as HTMLAnchorElement | null;
  if (open) {
    open.href = window.location.href;
    open.addEventListener("click", (e) => {
      // Prefer a top-level navigation out of the embed when possible.
      e.preventDefault();
      const url = window.location.href;
      try {
        if (window.top && window.top !== window.self) {
          window.top.location.href = url;
          return;
        }
      } catch {
        /* cross-origin top — fall through */
      }
      window.open(url, "_blank", "noopener,noreferrer");
    });
  }
  document
    .getElementById("constrained-shell-dismiss")
    ?.addEventListener("click", () => {
      banner.classList.add("hidden");
      try {
        sessionStorage.setItem(CONSTRAINED_SHELL_DISMISS_KEY, "1");
      } catch {
        /* ignore */
      }
    });
})();

/** Session-local: avoid re-firing staged tips mid-run after dismiss. */
let coachProgress: CoachProgress = loadCoachProgress();
/** Integrity sample for first-Will-drain detection. */
let lastIntegritySample = 0;
let pendingProclaimCoach = false;
let proclaimCoachTimer = 0;
let pendingAbideCoach = false;
let abideCoachTimer = 0;

function fightObjectiveLines(opts?: {
  mission?: MissionDef | null;
  endless?: boolean;
}): { objective: string; focus?: string; endless: boolean } {
  if (opts?.endless || playMode === "free") {
    return {
      objective: "Survive as long as you can — score is time held.",
      focus: "Integrity falls when a lie holds the Will. Clear Will first.",
      endless: true,
    };
  }
  const m = opts?.mission ?? null;
  return {
    objective: "Hold the Secret Place until the timer ends.",
    focus: m?.blurb,
    endless: false,
  };
}

function showFightObjective(opts?: {
  mission?: MissionDef | null;
  endless?: boolean;
}): void {
  const lines = fightObjectiveLines(opts);
  objectiveBanner?.show({
    objective: lines.objective,
    focus: lines.focus,
    autoHideMs: 10000,
  });
}

function pauseForCoach(): void {
  if (game.status !== "playing") return;
  if (!game.userPaused) {
    game.setUserPaused(true);
  }
  secretPlaceNote?.classList.add("is-teaching");
  syncUi(false);
}

function maybeShowIntroCoach(opts?: {
  mission?: MissionDef | null;
  endless?: boolean;
}): void {
  if (!coach || coachProgress.intro) return;
  if (game.status !== "playing") return;
  const lines = fightObjectiveLines(opts);
  pauseForCoach();
  coach.showIntro(
    {
      objective: lines.objective,
      focus: lines.focus,
      endless: lines.endless,
    },
    { pause: true },
  );
  syncPauseChrome();
}

function scheduleFirstProclaimCoach(): void {
  if (!coach || coachProgress.firstProclaim || pendingProclaimCoach) return;
  pendingProclaimCoach = true;
  window.clearTimeout(proclaimCoachTimer);
  // Let karaoke / lock bar appear so the tip lands while they feel the lock
  proclaimCoachTimer = window.setTimeout(() => {
    pendingProclaimCoach = false;
    if (!coach || coachProgress.firstProclaim) return;
    if (game.status !== "playing") return;
    if (coach.isOpen()) return;
    pauseForCoach();
    const answers = coachCardsFromDeck(isAnswerCoachCard);
    coach.showFirstProclaim({ pause: true, answers });
    syncPauseChrome();
  }, 900);
}

type CoachCardFace = {
  verseId: string;
  reference: string;
  category: string;
  speakSeconds?: number;
};

/** Unique verse faces from this run’s deck (hand + draw + discard pool). */
function coachCardsFromDeck(
  pred: (def: (typeof VERSES)[number]) => boolean,
): CoachCardFace[] {
  const seen = new Set<string>();
  const out: CoachCardFace[] = [];
  for (const id of game.deckPool) {
    if (seen.has(id)) continue;
    const def = VERSES.find((v) => v.id === id);
    if (!def || !pred(def)) continue;
    seen.add(id);
    out.push({
      verseId: def.id,
      reference: def.reference,
      category: def.category,
      speakSeconds: def.speakSeconds,
    });
  }
  // Prefer Guard first for Abide, short snipes first for Answers
  out.sort((a, b) => {
    if (a.verseId === "phil-4-6-7") return -1;
    if (b.verseId === "phil-4-6-7") return 1;
    return (a.speakSeconds ?? 99) - (b.speakSeconds ?? 99);
  });
  return out;
}

function isAnswerCoachCard(def: (typeof VERSES)[number]): boolean {
  if (def.kind !== "instant") return false;
  const sel = def.selector ?? "strongestLie";
  if (
    sel === "self" ||
    sel === "empower" ||
    sel === "restorePrayer" ||
    sel === "gatherPrayers"
  ) {
    return false;
  }
  // Enemy-facing tools: Answer / Soften / Chip / Redeem / Still-on-lie
  return (
    sel === "strongestLie" ||
    sel === "innermostLie" ||
    sel === "typePriority" ||
    sel === "layerChip" ||
    sel === "layerSlam" ||
    sel === "slowLie" ||
    sel === "convertLie" ||
    !!def.answerLevels
  );
}

function isAbideCoachCard(def: (typeof VERSES)[number]): boolean {
  return def.kind === "placeable";
}

function deckHasPlaceable(): boolean {
  return coachCardsFromDeck(isAbideCoachCard).length > 0;
}

/** After intro, when Prayers are in the fight deck — teach empty-slot Abide once. */
function maybeScheduleAbideCoach(): void {
  if (!coach || coachProgress.firstAbide || pendingAbideCoach) return;
  // Wait until intro is done so tips don't stack at fight start
  if (!coachProgress.intro) return;
  if (game.status !== "playing") return;
  if (coach.isOpen() || pendingProclaimCoach) return;
  if (!deckHasPlaceable()) return;

  pendingAbideCoach = true;
  window.clearTimeout(abideCoachTimer);
  abideCoachTimer = window.setTimeout(() => {
    pendingAbideCoach = false;
    if (!coach || coachProgress.firstAbide) return;
    if (game.status !== "playing") return;
    if (coach.isOpen()) return;
    const prayers = coachCardsFromDeck(isAbideCoachCard);
    if (!prayers.length) return;
    pauseForCoach();
    coach.showFirstAbide({ pause: true, prayers });
    syncPauseChrome();
  }, 1200);
}

/** Placed a Prayer without (or after) the tip — count as learned. */
function notePlaceablePlayed(kind: string | undefined): void {
  if (kind !== "placeable") return;
  if (coachProgress.firstAbide) return;
  coachProgress = markCoachStep("firstAbide", coachProgress);
  window.clearTimeout(abideCoachTimer);
  pendingAbideCoach = false;
}

function maybeShowWillDrainCoach(): void {
  if (!coach || coachProgress.firstWillDrain) return;
  if (game.status !== "playing") return;
  if (coach.isOpen()) return;
  const willEnemies = game.board.will.slots.some(
    (p) => p && p.side === "enemy",
  );
  if (!willEnemies) return;
  const integ = game.resources.integrity;
  if (integ >= lastIntegritySample - 0.01) return;
  // Real drain tick while Will occupied
  pauseForCoach();
  coach.showFirstWillDrain({ pause: true });
  syncPauseChrome();
}

function ownedWisdomCount(): number {
  return WISDOM_VERSE_IDS.reduce((n, id) => n + copiesOwned(id), 0);
}

function wisdomCoachCard(id: string) {
  const def = verseById(id);
  if (!def) return null;
  return {
    verseId: def.id,
    reference: def.reference,
    category: def.category,
    speakSeconds: def.speakSeconds,
  };
}

const PEEL_HINT: Record<Category, string> = {
  faith: "Mark 11:22 · Ps 56:3 · Heb 11:1",
  peace: "Rom 15:13 · Mark 4:39",
  wisdom: "James 1:5",
};

function peelAnswersInPool(pool: readonly string[], cat: Category): string[] {
  const ids: string[] = [];
  for (const id of pool) {
    const def = verseById(id);
    if (!def) continue;
    if (def.category !== cat) continue;
    if (!isDogmaPeelAnswer(def)) continue;
    if (!ids.includes(id)) ids.push(id);
  }
  return ids;
}

function buildPeelSteps(notice: {
  demand: Category;
  sequence: Category[];
}): {
  category: Category;
  label: string;
  current?: boolean;
  inDeck: boolean;
  owned: boolean;
  hint: string;
}[] {
  const seen = new Set<Category>();
  const pool = game.deckPool;
  const steps: ReturnType<typeof buildPeelSteps> = [];
  for (const cat of notice.sequence) {
    if (seen.has(cat)) continue;
    seen.add(cat);
    const inDeck = peelAnswersInPool(pool, cat).length > 0;
    const owned =
      inDeck ||
      VERSES.some(
        (v) =>
          v.category === cat && isDogmaPeelAnswer(v) && copiesOwned(v.id) > 0,
      );
    const twice =
      cat === "wisdom" &&
      notice.sequence.filter((c) => c === "wisdom").length >= 2;
    steps.push({
      category: cat,
      label: cat.charAt(0).toUpperCase() + cat.slice(1),
      current: cat === notice.demand,
      inDeck,
      owned,
      hint: twice ? `${PEEL_HINT[cat]} · needed twice` : PEEL_HINT[cat],
    });
  }
  return steps;
}

function maybeHandleDogmaNotice(): void {
  if (coach?.isOpen()) return;
  const notice = game.consumeDogmaNotice();
  if (!notice) return;

  const demand =
    notice.demand.charAt(0).toUpperCase() + notice.demand.slice(1);
  const giftDef = verseById(WISDOM_GIFT_ID);
  const owned = ownedWisdomCount();
  let gifted = false;
  if (owned <= 0 && giftDef) {
    grantCards([WISDOM_GIFT_ID]);
    gifted = true;
  }
  if (!notice.wisdomInDeck && giftDef) {
    game.addFieldFinding(WISDOM_GIFT_ID);
  }

  coachProgress = loadCoachProgress();
  if (coachProgress.firstSettled) return;

  if (!coach || coach.isOpen()) return;
  const peel = buildPeelSteps(notice);
  const missing = peel.filter((s) => !s.inDeck);
  const examples = [
    wisdomCoachCard(WISDOM_GIFT_ID),
    ...missing
      .filter((s) => s.category !== "wisdom")
      .map((s) => {
        const id = peelAnswersInPool(
          VERSES.filter((v) => copiesOwned(v.id) > 0).map((v) => v.id),
          s.category,
        )[0];
        return id ? wisdomCoachCard(id) : null;
      }),
  ].filter((c): c is NonNullable<typeof c> => !!c);

  const list: string[] = [
    `<strong>Matching color peels in one Word.</strong> Off-type only chips this shell like a III — three hits to grind it.`,
    `This shell wants <strong>${demand}</strong>. Chip / Still / Prayer will not peel.`,
  ];
  if (gifted) {
    list.push(
      `You had no Wisdom. <strong>${giftDef?.reference ?? "James 1:5"}</strong> is yours and on top of the draw pile.`,
    );
  } else if (missing.length) {
    const names = missing.map((s) => s.label).join(" and ");
    list.push(
      `This loadout has no ${names} Answer. Open <strong>Deck builder</strong> after the fight and add one.`,
    );
  }
  list.push(
    "Don’t let it sit on the Will. Echoes it leaves are Confusion — James 3 / 1 Cor 14:33 wipe those.",
  );

  pauseForCoach();
  coach.showDogmaNotice(
    {
      title: gifted ? "Settled — add Wisdom" : "Settled",
      lead: "Meets is a bonus on ordinary lies. Here the color is a lock: the wrong Answer only wears the shell.",
      list,
      note: missing.length
        ? "Deck builder: only an Answer of that category peels. Chips and keeps don’t count."
        : "You have every color in this deck — aim the matching Answer at Settled.",
    },
    { pause: true, cards: examples, peel },
  );
  coachProgress = markCoachStep("firstSettled", coachProgress);
  syncPauseChrome();
}

function openHowToFightSheet(): void {
  if (!coach) return;
  // Stay in user pause; sheet is reference, not a tutorial mark
  if (game.status === "playing" && !game.userPaused) {
    game.setUserPaused(true);
  }
  coach.showSheet({ pause: false });
  syncPauseChrome();
}

if (coach) {
  coach.setOnDismiss((step, wePaused) => {
    coachProgress = loadCoachProgress();
    if (wePaused && game.userPaused && game.status === "playing") {
      // Teaching steps auto-resume; sheet leaves pause menu state alone
      if (step !== "sheet") {
        game.setUserPaused(false);
      }
    }
    syncUi(false);
    // After intro (or any tip), try Abide once a Prayer is in hand
    if (step !== "sheet") {
      maybeScheduleAbideCoach();
    }
  });
}

function nextCampaignMission(
  fromId: string | null | undefined,
): MissionDef | null {
  if (!fromId) {
    const first = CAMPAIGN_MISSIONS.find((m) =>
      isMissionUnlocked(campaignSave, m.index, "neh-wall"),
    );
    return first ?? null;
  }
  const cur = missionById(fromId);
  if (!cur) return null;
  const camp = campaignForMissionId(fromId);
  const ladder = camp?.missions ?? CAMPAIGN_MISSIONS;
  const campId = camp?.id ?? "neh-wall";
  const next = ladder.find((m) => m.index === cur.index + 1);
  if (!next) return null;
  if (!isMissionUnlocked(campaignSave, next.index, campId)) return null;
  return next;
}

function endCause(
  won: boolean,
  stats: { integrityEnd: number; elapsed: number },
  endless: boolean,
): string {
  if (endless) {
    const base = `Survived ${formatSurviveTime(stats.elapsed)}. Integrity reached 0 — a lie held the Will.`;
    if (!coachProgress.firstDefeat) {
      coachProgress = markCoachStep("firstDefeat", coachProgress);
      return `${base} Next run: prioritize Will (bottom layer). Pause → How to fight anytime.`;
    }
    return base;
  }
  if (won) {
    return "You held integrity until the mission timer ended.";
  }
  if (stats.integrityEnd <= 0) {
    if (!coachProgress.firstDefeat) {
      coachProgress = markCoachStep("firstDefeat", coachProgress);
      return "A lie sat on the Will too long — integrity fell to 0. Next fight: clear Will first (bottom layer). Pause → How to fight for the full sheet.";
    }
    return "Integrity reached 0 — a lie held the Will too long. Clear Will first.";
  }
  return "Retreated from the fight.";
}

function freePlayScenario(difficulty: Difficulty) {
  const base =
    freeVariant === "muster" ? MUSTER_FREE_PLAY : ENDLESS_FREE_PLAY;
  return scaleScenario(base, difficulty);
}

/** Preferred deck for the next endless run (library id). */
let freePlayDeckId: string = getActiveDeckId();
/** Loadout this open-free-play run started from (for checkpoint tags). */
let freePlayRunDeckId: string | null = null;
let freePlayRunLoadoutKey: string | null = null;

function parseDeckFromUrl(): string | null {
  const q = new URLSearchParams(window.location.search).get("deck");
  if (!q || q === "active" || q === "current") return getActiveDeckId();
  if (getDeck(q)) return q;
  return null;
}

function refreshDeckSelect(): void {
  if (!deckSelect) return;
  const decks = listDecks();
  const active = getActiveDeckId();
  if (!decks.some((d) => d.id === freePlayDeckId)) {
    freePlayDeckId = active;
  }
  deckSelect.replaceChildren();
  for (const d of decks) {
    const opt = document.createElement("option");
    opt.value = d.id;
    const tag = d.id === active ? " · current" : "";
    opt.textContent = `${d.name} (${d.verseIds.length})${tag}`;
    deckSelect.appendChild(opt);
  }
  deckSelect.value = freePlayDeckId;
}

function resolveFreePlayLoadout(deckId?: string | null): {
  deckId: string;
  pool: string[];
  name: string;
} {
  const id =
    (deckId && getDeck(deckId)?.id) ||
    freePlayDeckId ||
    getActiveDeckId();
  const deck = loadDeck(id);
  setActiveDeck(deck.id);
  freePlayDeckId = deck.id;
  // Only owned verses are playable in endless
  const pool = resolvePlayableDeckById(ownedVerseIds(), deck.id);
  return { deckId: deck.id, pool, name: deck.name };
}

function startFreePlayRun(opts?: {
  deckId?: string | null;
  /** Full-state protocol snapshot (hybrid resume). Open free play only. */
  resume?: boolean;
}): void {
  const d = (difficultySelect?.value as Difficulty) || "standard";
  refreshDeckSelect();
  audio.resetMission();
  musterFindingOpen = false;
  hideMusterEndOverlay();

  // —— Muster climb ——
  if (freeVariant === "muster") {
    const muster = getMusterDeck();
    game.configureRun({
      difficulty: d,
      scenario: freePlayScenario(d),
      deckPool: muster,
    });
    botCtrl?.reset();
    handUi?.invalidate();
    boardDirty = true;
    endShown = false;
    pendingEndReport = null;
    if (statusPill) {
      statusPill.textContent = "Muster";
      statusPill.title = `Start deck · ${muster.length} cards`;
    }
    if (audioUnlocked) {
      audio.emit({ type: "mission.start" });
      void audio.preload(AUDIO_PRELOAD);
    }
    syncModeChrome();
    syncUi(true);
    lastIntegritySample = game.resources.integrity;
    showFightObjective({ endless: true });
    window.setTimeout(() => maybeShowIntroCoach({ endless: true }), 400);
    return;
  }

  // —— Open free play ——
  const loadout = resolveFreePlayLoadout(opts?.deckId);
  const saved = loadDeck(loadout.deckId);
  const cp =
    opts?.resume === true
      ? getResumeCheckpoint(d, saved)
      : null;
  const pool = cp?.deckPool?.length
    ? cp.deckPool.filter((id) => ownedVerseIds().includes(id))
    : loadout.pool;
  // Fallback if checkpoint deck was emptied by collection changes
  const deckPool = pool.length >= 3 ? pool : loadout.pool;

  game.configureRun({
    difficulty: d,
    scenario: freePlayScenario(d),
    deckPool,
  });

  let resumed = false;
  if (cp) {
    resumed = game.applyEndlessCheckpoint(cp);
    if (!resumed) {
      // Corrupt / incompatible snapshot — fresh open
      game.configureRun({
        difficulty: d,
        scenario: freePlayScenario(d),
        deckPool: loadout.pool,
      });
    }
  }

  if (resumed && cp) {
    freePlayRunDeckId = cp.deckId ?? loadout.deckId;
    freePlayRunLoadoutKey =
      cp.loadoutKey ?? loadoutKey(saved.verseIds);
  } else {
    freePlayRunDeckId = loadout.deckId;
    freePlayRunLoadoutKey = loadoutKey(saved.verseIds);
  }

  markDeckUsed(loadout.deckId);
  botCtrl?.reset();
  // handUi may not exist during early wiring; boot path always runs after HandUi init
  handUi?.invalidate();
  boardDirty = true;
  endShown = false;
  pendingEndReport = null;
  if (statusPill) {
    statusPill.textContent = resumed ? "Resume" : "Endless";
    statusPill.title = resumed
      ? `Checkpoint · ${formatCheckpointSummary(cp!)} · Deck: ${loadout.name}`
      : `Deck: ${loadout.name}`;
  }
  if (audioUnlocked) {
    audio.emit({ type: "mission.start" });
    void audio.preload(AUDIO_PRELOAD);
  }
  syncModeChrome();
  syncUi(true);
  lastIntegritySample = game.resources.integrity;
  showFightObjective({ endless: true });
  // Skip intro coach when resuming mid-pressure
  if (!resumed) {
    window.setTimeout(() => maybeShowIntroCoach({ endless: true }), 400);
  }
}

/**
 * Open the stage map focused on the right mission after leaving a run.
 * - fail / abandon → the stage just played
 * - success → the next unlocked stage (or the cleared finale if none)
 */
function returnToStageMap(
  missionId: string | null | undefined,
  outcome: "success" | "fail" | "abandon",
): void {
  let selectId = missionId ?? undefined;
  if (outcome === "success" && missionId) {
    selectId = nextCampaignMission(missionId)?.id ?? missionId;
  }
  openCampaignHub({
    view: "missions",
    selectMissionId: selectId,
    campaignId: missionId
      ? campaignForMissionId(missionId)?.id
      : undefined,
  });
}

function requestOpenCampaignHub(): void {
  const missionId = activeRun?.missionId;
  const midFight =
    !!activeRun && game.status === "playing" && playMode === "campaign";
  if (midFight) {
    const ok = window.confirm(
      "Abandon this mission and return to the campaign map?",
    );
    if (!ok) return;
    returnToStageMap(missionId, "abandon");
    return;
  }
  // End screen, story intro, or post-run chrome — stay on the stage map
  if (missionId && playMode === "campaign") {
    const outcome =
      game.status === "won"
        ? "success"
        : game.status === "lost"
          ? "fail"
          : "abandon";
    returnToStageMap(missionId, outcome);
    return;
  }
  openCampaignHub({ view: "library" });
}

function requestRestart(): void {
  if (game.status === "playing") {
    const ok = window.confirm("Restart this mission from the beginning?");
    if (!ok) return;
  }
  restart();
}

function goToNextStage(): void {
  const next = nextCampaignMission(activeRun?.missionId);
  if (!next) {
    returnToStageMap(activeRun?.missionId, "success");
    return;
  }
  endScreen.hide();
  // Keep last difficulty from active run or default standard
  const diff = activeRun?.difficulty ?? "standard";
  campaignSave = loadCampaign();
  beginCampaignMissionWithStory(next, diff);
}

const endScreen = new EndScreen({
  onMenu: () => {
    disposeAudio();
    window.location.href = "/";
  },
  onRestart: () => requestRestart(),
  onResume: () => {
    endScreen.hide();
    endShown = false;
    pendingEndReport = null;
    startFreePlayRun({ deckId: freePlayDeckId, resume: true });
  },
  onCampaign: () => {
    const missionId = activeRun?.missionId;
    const outcome = game.status === "won" ? "success" : "fail";
    returnToStageMap(missionId, outcome);
  },
  onNextStage: () => goToNextStage(),
});

const storyPanels = storyOverlay ? new StoryPanels(storyOverlay) : null;

function missionEverCleared(missionId: string): boolean {
  return (
    isDifficultyCleared(campaignSave, missionId, "easy") ||
    isDifficultyCleared(campaignSave, missionId, "standard") ||
    isDifficultyCleared(campaignSave, missionId, "hard")
  );
}

const campaignHub = campaignOverlay
  ? new CampaignHub(campaignOverlay, {
      onStart: (mission, difficulty) =>
        beginCampaignMissionWithStory(mission, difficulty),
      onViewChange: (state) => {
        syncHubUrl(state);
        syncModeChrome();
      },
    })
  : null;

function closeMusterFinding(): void {
  musterFindingOpen = false;
  rewardContext = null;
  pendingPickOptions = [];
  rewardPicker?.hide();
  if (game.status === "playing" && game.userPaused) {
    game.setUserPaused(false);
  }
  if (statusPill && game.status === "playing") {
    statusPill.textContent = freeVariant === "muster" ? "Muster" : "Endless";
    statusPill.title = game.waveLabel || "";
  }
  syncUi(false);
}

/**
 * Mid-run provision (mandatory pick, no decline):
 * - Field deck under max → choose 1 of 3 to add (on top of draw)
 * - Field deck at max → choose 1 of 3 from the deck to remove (make room)
 * Natural rhythm at the cap: add until full → lay aside → add → …
 */
function offerMusterProvision(waveLabel: string): void {
  if (!rewardPicker) return;
  const field = [...game.deckPool];
  const atMax = game.fieldDeckSize() >= MUSTER_MAX;

  if (atMax) {
    const options = buildMusterCullOptions(field, 3);
    if (options.length < 1) return;
    musterFindingOpen = true;
    rewardContext = "muster-cull";
    pendingPickOptions = options;
    if (!game.userPaused) game.setUserPaused(true);
    rewardPicker.showPick(options, {
      title: "Provision · lay aside",
      lead: `${waveLabel} — field deck is full (${MUSTER_MAX}). Choose one verse to remove from this run.`,
      appendPickHint: false,
      allowSkip: false,
      fieldPick: true,
    });
    if (statusPill) statusPill.textContent = "Lay aside";
    return;
  }

  const options = buildMusterFindingOptions(field, 3);
  if (!options.length) return;

  musterFindingOpen = true;
  rewardContext = "muster-finding";
  pendingPickOptions = options;
  if (!game.userPaused) game.setUserPaused(true);
  rewardPicker.showPick(options, {
    title: "Provision",
    lead: `${waveLabel} — choose one verse for this run. It goes on top of your draw pile.`,
    appendPickHint: false,
    allowSkip: false,
    fieldPick: true,
  });
  if (statusPill) statusPill.textContent = "Provision";
}

function hideMusterEndOverlay(): void {
  document.getElementById("muster-end")?.remove();
}

/** Compact tile for muster end edit (face + one action button). */
function makeMusterChoiceTile(
  verseId: string,
  opts: {
    action: "add" | "remove";
    copies?: number;
  },
): HTMLElement {
  const def = verseById(verseId);
  const tile = document.createElement("div");
  tile.className = `muster-choice-tile muster-choice-${opts.action}`;
  tile.dataset.verseId = verseId;

  if (def) {
    const face = createVerseCardFace({
      verseId: def.id,
      reference: def.reference,
      category: def.category,
      speakSeconds: def.speakSeconds ?? 2.5,
      metaLine: `${def.category} · ${def.kind}`,
      size: "deck",
    });
    face.classList.add("muster-choice-face");
    tile.appendChild(face);
  } else {
    const fallback = document.createElement("div");
    fallback.className = "muster-choice-fallback";
    fallback.textContent = verseId;
    tile.appendChild(fallback);
  }

  if (opts.copies !== undefined && opts.copies > 1) {
    const qty = document.createElement("span");
    qty.className = "muster-choice-qty";
    qty.textContent = `×${opts.copies}`;
    qty.title = `${opts.copies} copies in muster`;
    tile.appendChild(qty);
  }

  const act = document.createElement("button");
  act.type = "button";
  act.className =
    opts.action === "add" ? "btn primary btn-compact" : "btn secondary btn-compact";
  if (opts.action === "add") {
    act.dataset.musterAdd = verseId;
    act.textContent = "Inscribe";
    act.title = def
      ? `Add ${def.reference} to your start deck`
      : "Add to start deck";
  } else {
    act.dataset.musterRemove = verseId;
    act.textContent = "Remove";
    act.title = def
      ? `Remove one ${def.reference} from your start deck`
      : "Remove from start deck";
  }
  tile.appendChild(act);
  return tile;
}

/**
 * After a muster run: pick exactly one improvement (or skip).
 * Layout mirrors the reward picker — centered panel, tiled cards, clear CTAs.
 */
function showMusterEndEdit(payload: {
  won: boolean;
  lastMessage: string;
  stats: ReturnType<Game["getRunReport"]>["stats"];
  log: ReturnType<Game["getRunReport"]>["log"];
}): void {
  const finds = uniqueFindings(game.getFindingsThisRun());
  const muster = getMusterDeck();
  const canAdd = canInscribeToMuster() && finds.length > 0;
  const canRemove = canRemoveFromMuster();

  // Nothing to shape — go straight to end screen
  if (!canAdd && !canRemove) {
    const enriched = enrichEndPayload(payload);
    endScreen.show(enriched);
    if (statusPill) {
      statusPill.textContent = enriched.endless?.isNewBest ? "Best!" : "Run over";
    }
    syncModeChrome();
    return;
  }

  // Rebuild each time so we never stack listeners or stale cards
  document.getElementById("muster-end")?.remove();
  const root = document.createElement("div");
  root.id = "muster-end";
  root.className = "overlay muster-end-overlay";
  root.setAttribute("role", "dialog");
  root.setAttribute("aria-modal", "true");
  root.setAttribute("aria-labelledby", "muster-end-title");

  const card = document.createElement("div");
  card.className = "overlay-card muster-end-card";

  const header = document.createElement("header");
  header.className = "muster-end-header";
  const title = document.createElement("h2");
  title.id = "muster-end-title";
  title.textContent = "Shape your muster";
  const lead = document.createElement("p");
  lead.className = "muster-end-lead";
  lead.textContent = musterEndLead({
    canAdd,
    canRemove,
    findCount: finds.length,
  });
  const hint = document.createElement("p");
  hint.className = "muster-end-hint muted";
  hint.textContent = "Choose one change — or keep the start deck as it is.";
  header.append(title, lead, hint);

  const body = document.createElement("div");
  body.className = "muster-end-body";

  if (canAdd) {
    const sec = document.createElement("section");
    sec.className = "muster-end-section";
    sec.setAttribute("aria-label", "Inscribe a finding");
    const lab = document.createElement("h3");
    lab.className = "muster-end-section-label";
    lab.textContent = "Inscribe a finding";
    const sub = document.createElement("p");
    sub.className = "muster-end-section-sub muted";
    sub.textContent = "Add to your start deck for the next run.";
    const grid = document.createElement("div");
    grid.className = "muster-choice-grid";
    for (const id of finds) {
      grid.appendChild(makeMusterChoiceTile(id, { action: "add" }));
    }
    sec.append(lab, sub, grid);
    body.appendChild(sec);
  }

  if (canAdd && canRemove) {
    const or = document.createElement("p");
    or.className = "muster-end-or";
    or.setAttribute("aria-hidden", "true");
    or.textContent = "or";
    body.appendChild(or);
  }

  if (canRemove) {
    const sec = document.createElement("section");
    sec.className = "muster-end-section";
    sec.setAttribute("aria-label", "Remove from start deck");
    const lab = document.createElement("h3");
    lab.className = "muster-end-section-label";
    lab.textContent = "Remove from start deck";
    const sub = document.createElement("p");
    sub.className = "muster-end-section-sub muted";
    sub.textContent = "Trim dead weight so the draw stays sharp.";
    const grid = document.createElement("div");
    grid.className = "muster-choice-grid muster-choice-grid--muster";
    const counts = new Map<string, number>();
    for (const id of muster) counts.set(id, (counts.get(id) ?? 0) + 1);
    for (const id of counts.keys()) {
      grid.appendChild(
        makeMusterChoiceTile(id, {
          action: "remove",
          copies: counts.get(id),
        }),
      );
    }
    sec.append(lab, sub, grid);
    body.appendChild(sec);
  }

  const footer = document.createElement("footer");
  footer.className = "muster-end-footer";
  const skip = document.createElement("button");
  skip.type = "button";
  skip.className = "btn secondary";
  skip.dataset.musterSkip = "1";
  skip.textContent = "Keep muster as-is";
  footer.appendChild(skip);

  card.append(header, body, footer);
  root.appendChild(card);
  document.getElementById("app")?.appendChild(root);

  const finish = () => {
    root.remove();
    const enriched = enrichEndPayload(payload);
    endScreen.show(enriched);
    if (statusPill) {
      statusPill.textContent = enriched.endless?.isNewBest ? "Best!" : "Run over";
    }
    syncModeChrome();
  };

  root.addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    const add = t.closest("[data-muster-add]") as HTMLElement | null;
    if (add?.dataset.musterAdd) {
      e.preventDefault();
      inscribeToMuster(add.dataset.musterAdd);
      finish();
      return;
    }
    const rem = t.closest("[data-muster-remove]") as HTMLElement | null;
    if (rem?.dataset.musterRemove) {
      e.preventDefault();
      removeFromMuster(rem.dataset.musterRemove);
      finish();
      return;
    }
    if (t.closest("[data-muster-skip]")) {
      e.preventDefault();
      finish();
    }
  });

  if (statusPill) statusPill.textContent = "Muster";
}

/** What the open reward overlay is for (campaign vs endless packs / muster). */
let rewardContext:
  | "campaign"
  | "endless-pack"
  | "endless-art"
  | "muster-finding"
  | "muster-cull"
  | null = null;

const rewardPicker = rewardOverlay
  ? new RewardPicker(rewardOverlay, {
      onPick: (verseId) => {
        if (rewardContext === "muster-finding") {
          game.addFieldFinding(verseId);
          closeMusterFinding();
          handUi?.invalidate();
          boardDirty = true;
          syncUi(true);
          return;
        }
        if (rewardContext === "muster-cull") {
          game.removeFieldVerse(verseId);
          closeMusterFinding();
          handUi?.invalidate();
          boardDirty = true;
          syncUi(true);
          return;
        }

        if (rewardContext === "endless-pack") {
          const result = claimEndlessPackCard(verseId);
          pendingPickOptions = [];
          if (result.convertedToArt && result.artUnlocked.length) {
            const titles = result.artUnlocked
              .map(
                (id) =>
                  GALLERY_CHAPTER_PIECES.find((p) => p.id === id)?.title ?? id,
              )
              .join(" · ");
            rewardContext = "endless-art";
            rewardPicker?.showNotice({
              title: "Already at ×4",
              lead: `That verse is maxed — unlocked gallery keepsake: ${titles}. Open Gallery from the home menu anytime.`,
              continueLabel: "Continue",
            });
            return;
          }
          rewardContext = null;
          rewardPicker?.hide();
          flushPendingEnd();
          return;
        }

        if (rewardContext === "campaign" || (activeRun && pendingPickOptions.length)) {
          if (activeRun && pendingPickOptions.length) {
            campaignSave = applyRewardPick(
              campaignSave,
              verseId,
              pendingPickOptions,
              activeRun.missionId,
              activeRun.difficulty,
            );
          }
          rewardContext = null;
          pendingPickOptions = [];
          rewardPicker?.hide();
          flushPendingEnd();
          return;
        }

        pendingPickOptions = [];
        rewardPicker?.hide();
        flushPendingEnd();
      },
      onSkip: () => {
        if (
          rewardContext === "muster-finding" ||
          rewardContext === "muster-cull"
        ) {
          // Provisions are mandatory — ignore skip control if it still fires
          return;
        }
        if (rewardContext === "endless-pack") {
          skipEndlessPack();
          rewardContext = null;
          pendingPickOptions = [];
          rewardPicker?.hide();
          flushPendingEnd();
          return;
        }
        if (rewardContext === "endless-art") {
          rewardContext = null;
          rewardPicker?.hide();
          flushPendingEnd();
          return;
        }

        if (activeRun && pendingPickOptions.length) {
          // Skip still counts as a clear for this difficulty
          campaignSave = finalizeVictory(
            campaignSave,
            activeRun.missionId,
            activeRun.difficulty,
          );
        }
        rewardContext = null;
        pendingPickOptions = [];
        rewardPicker?.hide();
        flushPendingEnd();
      },
    })
  : null;

let pendingPickOptions: string[] = [];

/** Victory UI pipeline: optional outro panels, then rewards, then end screen. */
let pendingVictoryFlow: {
  payload: {
    won: boolean;
    lastMessage: string;
    stats: ReturnType<Game["getRunReport"]>["stats"];
    log: ReturnType<Game["getRunReport"]>["log"];
  };
  reward: RewardOutcome;
  mission: MissionDef;
} | null = null;

function reflectionLead(mission: MissionDef | undefined): string | undefined {
  return mission?.story?.reflection;
}

function presentReward(
  reward: RewardOutcome,
  mission: MissionDef | undefined,
): void {
  const lead = reflectionLead(mission);
  if (reward.kind === "pick" && rewardPicker) {
    rewardContext = "campaign";
    pendingPickOptions = reward.options;
    rewardPicker.showPick(reward.options, {
      lead,
      ownedCounts: campaignSave.ownedCounts,
      alsoGranted: reward.alsoGranted,
    });
    if (statusPill) statusPill.textContent = "Victory";
    return;
  }
  if (reward.kind === "auto" && rewardPicker) {
    rewardContext = "campaign";
    rewardPicker.showAuto(reward.granted, {
      ownedCounts: campaignSave.ownedCounts,
    });
    if (statusPill) statusPill.textContent = "Victory";
    return;
  }
  flushPendingEnd();
}

function continueVictoryAfterOutro(): void {
  const flow = pendingVictoryFlow;
  if (!flow) return;
  presentReward(flow.reward, flow.mission);
}

function startVictoryFlow(
  payload: NonNullable<typeof pendingVictoryFlow>["payload"],
  reward: RewardOutcome,
  mission: MissionDef,
): void {
  pendingEndReport = payload;
  pendingVictoryFlow = { payload, reward, mission };

  const outro = mission.story?.outro;
  if (outro?.length && storyPanels) {
    if (statusPill) statusPill.textContent = "Story";
    storyPanels.show(mission.story!, {
      mode: "outro",
      canSkip: true,
      onComplete: () => continueVictoryAfterOutro(),
    });
    return;
  }
  presentReward(reward, mission);
}

// --- Audio: one-shots + state beds + environmental music + ducking + settings ---
let audio: AudioEngine = createAudioEngine();
let audioUnlocked = false;
let audioPanel: AudioSettingsPanelHandle | null = null;

const proclaimAnnounce = document.getElementById("proclaim-announce");
let announceClearTimer: ReturnType<typeof setTimeout> | null = null;

function announceProclaimStart(ref: string, text: string): void {
  if (!proclaimAnnounce) return;
  // One polite update for the full line — never word-by-word karaoke spam
  proclaimAnnounce.textContent = `Proclaiming ${ref}. ${text}`;
  if (announceClearTimer) clearTimeout(announceClearTimer);
  announceClearTimer = setTimeout(() => {
    if (proclaimAnnounce) proclaimAnnounce.textContent = "";
    announceClearTimer = null;
  }, 4000);
}

// Single listener for mission lifetime (Game keeps the array across reset).
// Free-play: keep farthest stable protocol snapshot for hybrid resume
game.onEndlessCheckpoint((payload) => {
  if (freeVariant === "muster") return;
  saveEndlessCheckpoint({
    ...payload,
    deckId: freePlayRunDeckId ?? undefined,
    loadoutKey: freePlayRunLoadoutKey ?? undefined,
  });
});

// Muster: provision at each protocol boundary
game.onProtocolBoundary((info) => {
  if (playMode !== "free" || freeVariant !== "muster") return;
  if (game.status !== "playing") return;
  if (musterFindingOpen) return;
  // Muster has no soft open — first protocol lands almost immediately.
  // Wait until the first protocol *advance* so the run has time to breathe.
  if (info.isFirst) return;
  offerMusterProvision(info.waveLabel);
});

game.onAudioEvent((e) => {
  audio.emit(e);
  if (e.type === "proclaim.start") {
    const line =
      game.proclaimText ||
      // fallback if text not yet mirrored (should be set in beginProclaim)
      e.ref;
    announceProclaimStart(e.ref, line);
  }
  if (e.type === "proclaim.end" && proclaimAnnounce) {
    // Soft clear; timer may also clear
    if (announceClearTimer) {
      clearTimeout(announceClearTimer);
      announceClearTimer = null;
    }
    proclaimAnnounce.textContent = "";
  }
});

const AUDIO_PRELOAD = [
  ...PRELOAD_COMBAT_IDS,
  ...PRELOAD_STATE_IDS,
  ...PRELOAD_MUSIC_IDS,
  // Loads all Proclaim VO clips + words.json (see VoicePlayer)
  "preload:proclaim-vo",
];

/** First user gesture unlocks AudioContext (autoplay policy). */
function unlockAudio(): void {
  if (audioUnlocked) return;
  audioUnlocked = true;
  void (async () => {
    try {
      await audio.resume();
      await audio.preload(AUDIO_PRELOAD);
      // Karaoke word timings from VO when available
      game.setProclaimKaraokeDriver(audio.createKaraokeDriver());
      // Resume + kick again so music loops start on a running context
      await audio.resume();
      if (game.status === "playing" && game.elapsed < 2) {
        audio.emit({ type: "mission.start" });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[audio] unlock/preload failed", err);
    }
  })();
}

audioPanel = bindAudioSettingsPanel(document, audio, {
  onUserChange: () => unlockAudio(),
});

// Study lightbox: Hear spoken Word (loads on demand if not preloaded yet)
cardFullscreen.setVoPreview({
  play: async (verseId) => {
    unlockAudio();
    await audio.resume();
    return audio.playVerseVo(verseId);
  },
  stop: () => audio.stopVerseVo(),
  isPlaying: (verseId) => audio.isVerseVoPlaying(verseId),
});

function onFirstGesture(): void {
  unlockAudio();
  window.removeEventListener("pointerdown", onFirstGesture, true);
  window.removeEventListener("keydown", onFirstGesture, true);
}
window.addEventListener("pointerdown", onFirstGesture, true);
window.addEventListener("keydown", onFirstGesture, true);

/** Tab hide: suspend beds/context; show: resume if game not user-paused. */
function onVisibilityChange(): void {
  if (document.hidden) {
    void audio.suspend();
  } else if (!game.userPaused && game.status === "playing") {
    void audio.resume();
  }
}
document.addEventListener("visibilitychange", onVisibilityChange);

function disposeAudio(): void {
  document.removeEventListener("visibilitychange", onVisibilityChange);
  window.removeEventListener("pointerdown", onFirstGesture, true);
  window.removeEventListener("keydown", onFirstGesture, true);
  audioPanel?.dispose();
  audioPanel = null;
  audio.dispose();
}

window.addEventListener("pagehide", () => {
  disposeAudio();
});

game.onMessage((msg, entry) => discourse.push(msg, entry));

const difficultySelect = document.getElementById(
  "difficulty-select",
) as HTMLSelectElement | null;
const deckSelect = document.getElementById(
  "deck-select",
) as HTMLSelectElement | null;
const botSelect = document.getElementById(
  "bot-select",
) as HTMLSelectElement | null;
const statusPill = document.getElementById("status-pill");
const pauseBtn = document.getElementById("btn-pause") as HTMLButtonElement | null;
const boardStage = document.querySelector(".board-stage");

let boardDirty = true;
let dragActive = false;
/** Drop while speaking: fire when lock ends */
let queuedDrop: {
  intent: { layer: import("./game/board/types").LayerId; slot: number };
  cardId: string;
  reference: string;
} | null = null;
let botId: BotId = "off";
let botCtrl: BotController | null = null;

const queueBanner = document.getElementById("queue-banner");
const queueRefEl = document.getElementById("queue-ref");
const queueCancelBtn = document.getElementById(
  "queue-cancel",
) as HTMLButtonElement | null;

function parseBotFromUrl(): BotId {
  const q = new URLSearchParams(window.location.search).get("bot");
  if (q === "hold-will" || q === "1") return "hold-will";
  if (q === "learner" || q === "2") return "learner";
  if (q === "random") return "random";
  return "off";
}

function setBot(id: BotId): void {
  botId = id;
  botCtrl = id === "off" ? null : createBotController(id);
  botCtrl?.reset();
  if (botId !== "off" && game.userPaused) {
    game.setUserPaused(false);
  }
  // Close any stuck reveal when switching
  if (reveal.isOpen()) {
    game.closeReveal();
    reveal.hide();
  }
}

botId = parseBotFromUrl();

const boardUi = new BoardUi(boardRoot, {
  onDrop: (intent, cardId) => {
    // Clear drag flag before re-render so a destroyed drag source cannot leave
    // the board stuck ignoring later drags.
    dragActive = false;
    boardUi.setDragActive(false);

    unlockAudio();

    if (game.isProclaiming()) {
      const ref =
        game.hand.find((c) => c.instanceId === cardId)?.def.reference ?? "verse";
      queuedDrop = { intent, cardId, reference: ref };
      handUi.setQueuedId(cardId);
      handUi.invalidate();
      audio.emit({ type: "ui.queue", reference: ref });
      syncUi(true);
      return;
    }

    const preDef = game.hand.find((c) => c.instanceId === cardId)?.def;
    const ok = game.activate(cardId, intent);
    clearQueueUi();
    handUi.invalidate();
    boardDirty = true;
    syncUi(true);
    if (ok) {
      objectiveBanner?.hide();
      notePlaceablePlayed(preDef?.kind);
      scheduleFirstProclaimCoach();
      maybeShowEnd();
    }
  },
  onInspectEnemy: (presence, layer, slot) => {
    lieInspect.hideHover();
    lieInspect.show(presence, layer, slot);
  },
  onHoverEnemy: (presence, _layer, _slot, x, y) => {
    if (dragActive) {
      lieInspect.hideHover();
      return;
    }
    if (!presence) {
      lieInspect.hideHover();
      return;
    }
    lieInspect.showHover(presence, x, y);
  },
});

const reveal = new RevealModal({
  onClose: () => {
    game.closeReveal();
    reveal.hide();
    syncUi(false);
  },
});

if (botSelect) {
  botSelect.value = botId;
  botSelect.addEventListener("change", () => {
    setBot((botSelect.value as BotId) || "off");
    syncUi(false);
  });
}
if (botId !== "off") setBot(botId);

const handUi = new HandUi(handRoot, {
  onDragStart: (instanceId) => {
    dragActive = true;
    const card = game.hand.find((c) => c.instanceId === instanceId);
    const aim = card ? dragAimMode(card.def) : "slot";
    const presenceSide =
      card?.def.selector === "restorePrayer" ||
      card?.def.selector === "gatherPrayers"
        ? "player"
        : "enemy";
    let legal = null;
    try {
      legal = card ? planLegalTargets(game.board, card.def) : null;
    } catch (err) {
      console.error("planLegalTargets failed", err);
    }
    boardUi.setDragActive(true, aim, instanceId, { presenceSide, legal });
    lieInspect.hideHover();
  },
  onDragMove: (clientX, clientY) => {
    boardUi.updateDragAt(clientX, clientY);
  },
  onDragEnd: () => {
    dragActive = false;
    boardUi.setDragActive(false);
  },
  onDropAt: (clientX, clientY, _instanceId) => {
    // Pointer path (touch/pen): resolve via live dragCardId on the board.
    boardUi.resolveDrop(clientX, clientY, null);
  },
  onDropOnCard: (playedId, targetId) => {
    const card = game.hand.find((c) => c.instanceId === playedId);
    if (!card?.def.layAside) return;
    game.activate(playedId, {
      layer: "mind",
      slot: 0,
      layAsideInstanceId: targetId,
    });
  },
});

// Stage-level drop backup (when pointer is between slots / over chrome)
const boardStageEl = document.querySelector(".board-stage");
boardStageEl?.addEventListener("dragover", (e) => {
  if (!dragActive) return;
  e.preventDefault();
  if (e instanceof DragEvent && e.dataTransfer) {
    e.dataTransfer.dropEffect = "move";
  }
});
boardStageEl?.addEventListener("drop", (e) => {
  if (!dragActive || !(e instanceof DragEvent)) return;
  e.preventDefault();
  boardUi.resolveDrop(e.clientX, e.clientY, e.dataTransfer);
});

// Phase C: discrete board VFX from Game commits (strikes, silence, redeem, …)
game.onBoardFx((e) => boardUi.playFx(e));

// Phase D: combat feedback prefs (motion + discourse)
boardUi.setCombatFeedback(combatFeedbackPanel.getSettings());
combatFeedbackPanel.onChange((s) => {
  boardUi.setCombatFeedback(s);
  discourse.setLevel(s.discourse);
});
// Re-evaluate effective motion when OS reduced-motion preference flips
try {
  const mq = matchMedia("(prefers-reduced-motion: reduce)");
  const onMq = () => boardUi.setCombatFeedback(combatFeedbackPanel.getSettings());
  if (typeof mq.addEventListener === "function") {
    mq.addEventListener("change", onMq);
  } else if (typeof mq.addListener === "function") {
    mq.addListener(onMq);
  }
} catch {
  /* ignore */
}
// Panel DOM + re-broadcast to BoardUi / discourse (onChange already registered)
combatFeedbackPanel.syncFromStorage();

function setUrlMode(mode: "campaign" | "free" | "muster"): void {
  const url = new URL(window.location.href);
  url.searchParams.set("mode", mode);
  if (mode === "free" || mode === "muster") {
    url.searchParams.delete("view");
    url.searchParams.delete("campaign");
    url.searchParams.delete("mission");
  }
  window.history.replaceState({}, "", url.toString());
}

/** Keep hub deep-links shareable: library has no mission; map keeps campaign + stage. */
function syncHubUrl(state: {
  view: "library" | "missions";
  campaignId: string;
  selectMissionId: string | null;
}): void {
  const url = new URL(window.location.href);
  url.searchParams.set("mode", "campaign");
  if (state.view === "library") {
    url.searchParams.delete("view");
    url.searchParams.delete("campaign");
    url.searchParams.delete("mission");
  } else {
    url.searchParams.set("view", "missions");
    url.searchParams.set("campaign", state.campaignId);
    if (state.selectMissionId) {
      url.searchParams.set("mission", state.selectMissionId);
    } else {
      url.searchParams.delete("mission");
    }
  }
  window.history.replaceState({}, "", url.toString());
}

// --- Shell modes: hub is a place, not a modal over a live fight ---

/**
 * Proclaim UI shells (mutually exclusive for chrome + sim ticking):
 * - hub: campaign library / stage map (combat parked + hidden)
 * - story: intro/outro panels
 * - combat: board + hand + fight chrome
 */
type ShellMode = "hub" | "story" | "combat";

function getShellMode(): ShellMode {
  if (campaignHub?.isOpen()) return "hub";
  if (storyPanels?.isOpen()) return "story";
  return "combat";
}

function isHubShell(): boolean {
  return getShellMode() === "hub";
}

/** Freeze and clear fight chrome so returning to the hub never feels mid-battle. */
function parkCombatForShell(): void {
  game.setUserPaused(true);
  clearQueueUi();
  endScreen.hide();
  rewardPicker?.hide();
  reveal.hide();
  lieInspect.hide();
  lieInspect.hideHover();
  discourse.clear();
  objectiveBanner?.hide();
  pauseBanner?.classList.add("hidden");
  if (coach?.isOpen()) coach.dismiss();
}

const backLink = document.querySelector(
  ".brand .back-link",
) as HTMLAnchorElement | null;

/**
 * Combat top-left back (hub has its own breadcrumbs):
 * battle → stage map · free play → free-play hub
 */
function syncBackLink(): void {
  if (!backLink) return;
  // Hub owns navigation; combat chrome is hidden there.
  if (isHubShell()) {
    backLink.textContent = "← Menu";
    backLink.title = "Main menu";
    backLink.href = "/";
    delete backLink.dataset.nav;
    return;
  }

  const inCampaignMission =
    playMode === "campaign" && !!activeRun && getShellMode() === "combat";
  const inFreePlay = playMode === "free";
  const inStory =
    getShellMode() === "story" && playMode === "campaign" && !!activeRun;

  if (inCampaignMission || inStory) {
    backLink.textContent = "← Map";
    backLink.title = "Back to stage map";
    backLink.href = "#";
    backLink.dataset.nav = "map";
  } else if (inFreePlay) {
    backLink.textContent = "← Free play";
    backLink.title = "Back to free play modes";
    backLink.href =
      freeVariant === "muster" ? "/freeplay/?mode=muster" : "/freeplay/";
    delete backLink.dataset.nav;
  } else {
    backLink.textContent = "← Menu";
    backLink.title = "Main menu";
    backLink.href = "/";
    delete backLink.dataset.nav;
  }
}

backLink?.addEventListener("click", (e) => {
  const nav = (e.currentTarget as HTMLAnchorElement).dataset.nav;
  if (nav === "map") {
    e.preventDefault();
    requestOpenCampaignHub();
  } else if (nav === "library") {
    e.preventDefault();
    openCampaignHub({ view: "library" });
  }
});

function syncModeChrome(): void {
  const shell = getShellMode();
  const inCampaignRun =
    playMode === "campaign" && !!activeRun && shell === "combat";
  const onHub = shell === "hub";

  document.body.dataset.shell = shell;
  document.body.classList.toggle("shell-hub", shell === "hub");
  document.body.classList.toggle("shell-story", shell === "story");
  document.body.classList.toggle("shell-combat", shell === "combat");
  document.body.classList.toggle("mode-campaign", playMode === "campaign");
  document.body.classList.toggle("mode-free", playMode === "free");
  document.body.classList.toggle(
    "mode-muster",
    playMode === "free" && freeVariant === "muster",
  );

  // Mark combat regions for a11y while hub/story own the screen (overlay is inside #app)
  const combatVisible = shell === "combat";
  for (const sel of [".board-stage", ".hand", ".top-bar", ".proclaim-float"]) {
    const el = document.querySelector(sel);
    if (!el) continue;
    el.setAttribute("aria-hidden", combatVisible ? "false" : "true");
  }

  if (difficultyWrap) {
    difficultyWrap.classList.toggle("hidden", playMode === "campaign");
  }
  // Open free play uses deck library; Muster uses the persistent start deck
  if (deckSelect) {
    const deckWrap =
      deckSelect.closest("label") ?? deckSelect.parentElement;
    deckWrap?.classList.toggle(
      "hidden",
      playMode !== "free" || freeVariant === "muster",
    );
  }
  if (btnCampaign) {
    btnCampaign.classList.toggle("hidden", onHub || shell === "story");
    btnCampaign.textContent = inCampaignRun
      ? "Map"
      : playMode === "campaign"
        ? "Map"
        : "Campaign";
  }
  // Pause menu abandon only in campaign runs
  const abandonBtn = document.getElementById("btn-pause-map");
  abandonBtn?.classList.toggle("hidden", playMode !== "campaign");
  syncBackLink();
}

/**
 * Enter the campaign hub as its own shell (library or stage map).
 * Parks combat so stage select is not a modal over a half-finished fight.
 */
function openCampaignHub(opts?: {
  /** library = pick campaign (default); missions = stage map for a campaign */
  view?: "library" | "missions";
  campaignId?: string;
  selectMissionId?: string;
}): void {
  playMode = "campaign";
  activeRun = null;
  endShown = false;
  pendingEndReport = null;
  pendingVictoryFlow = null;
  rewardContext = null;
  pendingPickOptions = [];
  parkCombatForShell();
  storyPanels?.hide();

  campaignSave = loadCampaign();
  const view = opts?.view ?? "library";
  campaignHub?.render(campaignSave, {
    view,
    campaignId: opts?.campaignId,
    selectMissionId: opts?.selectMissionId,
  });
  campaignHub?.show();
  // URL synced via CampaignHub onViewChange
  syncModeChrome();
}

/**
 * Enter open free-play shell. If a stable checkpoint exists and the caller
 * did not force a mode, offer Resume vs New run before starting.
 */
function enterFreePlay(opts?: {
  resume?: boolean;
  forceNew?: boolean;
  deckId?: string | null;
}): void {
  playMode = "free";
  freeVariant = "open";
  activeRun = null;
  setUrlMode("free");
  campaignHub?.hide();
  storyPanels?.hide();
  rewardPicker?.hide();
  hideMusterEndOverlay();
  pendingVictoryFlow = null;
  endScreen.hide();
  reveal.hide();

  const d = (difficultySelect?.value as Difficulty) || "standard";
  const deck = loadDeck(opts?.deckId ?? freePlayDeckId);
  const cp = getResumeCheckpoint(d, deck);
  const wantResume = opts?.resume === true;
  const forceNew = opts?.forceNew === true;

  if (!forceNew && !wantResume && cp && opts?.resume === undefined) {
    // Hub / deep-link with a checkpoint — ask once
    showFreePlayStartChooser(cp);
    syncModeChrome();
    return;
  }

  startFreePlayRun({
    deckId: opts?.deckId ?? freePlayDeckId,
    resume: wantResume && !forceNew,
  });
  syncModeChrome();
}

/** Muster climb — thin start deck, provisions, end-of-run shape. */
function enterMuster(): void {
  playMode = "free";
  freeVariant = "muster";
  activeRun = null;
  setUrlMode("muster");
  campaignHub?.hide();
  storyPanels?.hide();
  rewardPicker?.hide();
  hideMusterEndOverlay();
  pendingVictoryFlow = null;
  endScreen.hide();
  reveal.hide();
  startFreePlayRun();
  syncModeChrome();
}

function isFreePlayStartOpen(): boolean {
  const el = document.getElementById("free-play-start");
  return !!el && !el.classList.contains("hidden");
}

function hideFreePlayStartChooser(): void {
  document.getElementById("free-play-start")?.classList.add("hidden");
}

function resolveFreePlayStartChoice(act: "resume" | "new" | "back"): void {
  hideFreePlayStartChooser();
  if (act === "resume") {
    startFreePlayRun({ deckId: freePlayDeckId, resume: true });
    syncModeChrome();
  } else if (act === "new") {
    startFreePlayRun({ deckId: freePlayDeckId, resume: false });
    syncModeChrome();
  } else {
    window.location.href = "/freeplay/";
  }
}

/** Centered overlay when free play has a stable protocol checkpoint. */
function showFreePlayStartChooser(
  cp: NonNullable<ReturnType<typeof getEndlessCheckpoint>>,
): void {
  parkCombatForShell();
  let root = document.getElementById("free-play-start");
  if (!root) {
    root = document.createElement("div");
    root.id = "free-play-start";
    root.className = "overlay free-play-start-overlay";
    root.setAttribute("role", "dialog");
    root.setAttribute("aria-modal", "true");
    root.setAttribute("aria-labelledby", "free-play-start-title");
    root.innerHTML = `
      <div class="overlay-card free-play-start-card">
        <p class="free-play-start-kicker">Hold the Line</p>
        <h2 class="free-play-start-title" id="free-play-start-title">Checkpoint ready</h2>
        <div class="free-play-start-cp">
          <span class="free-play-start-cp-label">Last stable protocol</span>
          <strong class="free-play-start-cp-value"></strong>
          <span class="free-play-start-cp-time"></span>
        </div>
        <p class="free-play-start-note">
          Resume restores the full board — lies, prayers, hand, and integrity —
          at this protocol. New run starts from the beginning.
        </p>
        <div class="free-play-start-actions"></div>
      </div>
    `;
    root.addEventListener("click", (e) => {
      const t = (e.target as HTMLElement).closest("[data-fp-act]") as
        | HTMLElement
        | null;
      if (!t) return;
      const act = t.dataset.fpAct;
      if (act === "resume" || act === "new" || act === "back") {
        resolveFreePlayStartChoice(act);
      }
    });
    document.addEventListener("keydown", (e) => {
      if (e.key !== "Escape" || !isFreePlayStartOpen()) return;
      e.preventDefault();
      resolveFreePlayStartChoice("back");
    });
    document.getElementById("app")?.appendChild(root);
  }

  const label = root.querySelector(".free-play-start-cp-value");
  if (label) {
    label.textContent = cp.waveLabel?.trim() || "Checkpoint";
  }
  const time = root.querySelector(".free-play-start-cp-time");
  if (time) {
    time.textContent = `${formatSurviveTime(cp.elapsed)} held`;
  }
  const actions = root.querySelector(".free-play-start-actions");
  if (actions) {
    actions.replaceChildren();
    const resume = document.createElement("button");
    resume.type = "button";
    resume.className = "btn primary";
    resume.dataset.fpAct = "resume";
    resume.textContent = `Resume · ${cp.waveLabel?.trim() || "checkpoint"}`;
    const fresh = document.createElement("button");
    fresh.type = "button";
    fresh.className = "btn secondary";
    fresh.dataset.fpAct = "new";
    fresh.textContent = "New run";
    const back = document.createElement("button");
    back.type = "button";
    back.className = "btn secondary";
    back.dataset.fpAct = "back";
    back.textContent = "Back to free play";
    actions.append(resume, fresh, back);
  }

  root.classList.remove("hidden");
  const resumeBtn = root.querySelector(
    "[data-fp-act='resume']",
  ) as HTMLButtonElement | null;
  resumeBtn?.focus();
}

/** Intro panels (first clear: no skip; replays: skippable), then combat. */
function beginCampaignMissionWithStory(
  mission: MissionDef,
  difficulty: CampaignDifficulty,
): void {
  // Track the mission early so Map / ← Map return to the stage map during intro.
  playMode = "campaign";
  activeRun = { missionId: mission.id, difficulty };
  setUrlMode("campaign");

  const story = mission.story;
  if (!story?.intro.length || !storyPanels) {
    startCampaignMission(mission, difficulty);
    return;
  }
  campaignHub?.hide();
  parkCombatForShell();
  endScreen.hide();
  const canSkip = missionEverCleared(mission.id);
  if (statusPill) statusPill.textContent = "Story";
  storyPanels.show(story, {
    mode: "intro",
    canSkip,
    onComplete: () => startCampaignMission(mission, difficulty),
  });
  syncModeChrome();
}

function startCampaignMission(
  mission: MissionDef,
  difficulty: CampaignDifficulty,
): void {
  playMode = "campaign";
  activeRun = { missionId: mission.id, difficulty };
  setUrlMode("campaign");
  campaignHub?.hide();
  storyPanels?.hide();
  rewardPicker?.hide();
  endScreen.hide();
  reveal.hide();
  lieInspect.hide();
  lieInspect.hideHover();
  discourse.clear();
  endShown = false;
  pendingEndReport = null;
  pendingVictoryFlow = null;
  clearQueueUi();

  const scenario = scaleScenario(mission.scenario, difficulty);
  const deckPool = resolveMissionLoadout(mission, campaignSave);

  audio.resetMission();
  game.configureRun({ difficulty, scenario, deckPool });
  botCtrl?.reset();
  handUi.invalidate();
  boardDirty = true;
  if (statusPill) statusPill.textContent = "Playing";
  if (audioUnlocked) {
    audio.emit({ type: "mission.start" });
    void audio.preload(AUDIO_PRELOAD);
  }
  syncModeChrome();
  syncUi(true);
  lastIntegritySample = game.resources.integrity;
  // Early stages keep the Secret Place teaching note visible under integrity
  if (mission.index <= 1 || !coachProgress.intro) {
    secretPlaceNote?.classList.add("is-teaching");
  }
  showFightObjective({ mission });
  window.setTimeout(() => maybeShowIntroCoach({ mission }), 400);
}

function handleCampaignVictory(): RewardOutcome {
  if (!activeRun) return { kind: "none" };
  const result = processVictory(
    campaignSave,
    activeRun.missionId,
    activeRun.difficulty,
  );
  campaignSave = result.save;
  return result.reward;
}

function enrichEndPayload(report: {
  won: boolean;
  lastMessage: string;
  stats: ReturnType<Game["getRunReport"]>["stats"];
  log: ReturnType<Game["getRunReport"]>["log"];
}) {
  const next =
    playMode === "campaign" && report.won
      ? nextCampaignMission(activeRun?.missionId)
      : null;
  const endless = playMode === "free" && game.isEndless();
  let endlessScore:
    | {
        survived: number;
        best: number;
        isNewBest: boolean;
        note?: string;
        checkpoint?: { label: string; elapsed: number } | null;
      }
    | undefined;
  if (endless) {
    if (freeVariant === "muster") {
      const recorded = recordMusterScore(
        report.stats.difficulty,
        report.stats.elapsed,
      );
      const finds = uniqueFindings(game.getFindingsThisRun()).length;
      endlessScore = {
        survived: report.stats.elapsed,
        best: recorded.best,
        isNewBest: recorded.isNewBest,
        note:
          finds > 0
            ? `Muster climb · ${finds} provision${finds === 1 ? "" : "s"} this run`
            : "Muster climb · thin start deck under protocol pressure",
        checkpoint: null,
      };
    } else {
      const recorded = recordEndlessScore(
        report.stats.difficulty,
        report.stats.elapsed,
      );
      const t = report.stats.elapsed;
      let note: string | undefined;
      if (t < PACK_SURVIVE_SECONDS) {
        note =
          t >= PACK_PITY_SURVIVE_SECONDS
            ? `Survive ${PACK_SURVIVE_SECONDS}s for a verse pack (pity after a few short runs).`
            : `Survive ${PACK_SURVIVE_SECONDS}s for a verse pack reward.`;
      }
      const cp = getEndlessCheckpoint(report.stats.difficulty);
      endlessScore = {
        survived: report.stats.elapsed,
        best: recorded.best,
        isNewBest: recorded.isNewBest,
        note,
        checkpoint: cp
          ? { label: cp.waveLabel || "Checkpoint", elapsed: cp.elapsed }
          : null,
      };
    }
  }
  return {
    ...report,
    cause: endCause(report.won, report.stats, endless),
    endless: endlessScore,
    campaign: {
      enabled: playMode === "campaign",
      nextMissionName: next?.name ?? null,
    },
  };
}

function flushPendingEnd(): void {
  if (!pendingEndReport) return;
  const report = pendingEndReport;
  pendingEndReport = null;
  const payload = enrichEndPayload(report);
  endScreen.show(payload);
  if (statusPill) {
    statusPill.textContent = payload.endless
      ? payload.endless.isNewBest
        ? "Best!"
        : "Run over"
      : report.won
        ? "Victory"
        : "Retreated";
  }
  syncModeChrome();
}

function restart(): void {
  reveal.hide();
  lieInspect.hide();
  lieInspect.hideHover();
  endScreen.hide();
  rewardPicker?.hide();
  rewardContext = null;
  pendingPickOptions = [];
  discourse.clear();
  endShown = false;
  pendingEndReport = null;
  clearQueueUi();
  audio.resetMission();

  if (playMode === "campaign" && activeRun) {
    const m = missionById(activeRun.missionId);
    if (m) {
      startCampaignMission(m, activeRun.difficulty);
      return;
    }
  }

  // Free play Restart / Play again = new run from the beginning
  startFreePlayRun({ deckId: freePlayDeckId, resume: false });
}

function togglePause(): void {
  if (game.status !== "playing") return;
  game.toggleUserPause();
  syncUi(false);
}

function syncPauseChrome(): void {
  const paused = game.paused;
  const user = game.userPaused;
  const coachOpen = coach?.isOpen() ?? false;
  if (pauseBtn) {
    pauseBtn.textContent = user ? "Resume" : "Pause";
    pauseBtn.classList.toggle("pause-active", user);
    pauseBtn.disabled = game.status !== "playing";
  }
  if (pauseBanner) {
    // Full pause menu only for user pause (not Easy reveal freezes / coach)
    const showMenu = user && !coachOpen;
    pauseBanner.classList.toggle("hidden", !showMenu);
    const hint = pauseBanner.querySelector(".pause-hint");
    if (hint) {
      hint.textContent = "Space or Resume to continue";
    }
  }
  boardStage?.classList.toggle("is-paused", paused);
  if (statusPill && game.status === "playing") {
    if (paused) statusPill.textContent = "Paused";
    else if (botId !== "off" && playMode === "free") {
      statusPill.textContent =
        botId === "random"
          ? "Random"
          : botId === "learner"
            ? "Learner"
            : "Bot";
      statusPill.title = botCtrl?.lastReason || BOT_LABELS[botId];
    } else if (playMode === "free") {
      statusPill.textContent =
        freeVariant === "muster" ? "Muster" : "Endless";
      statusPill.title = game.waveLabel || "";
    } else {
      statusPill.textContent = "Playing";
      statusPill.title = "";
    }
  }
}

document.getElementById("btn-restart")?.addEventListener("click", () => {
  requestRestart();
});
btnCampaign?.addEventListener("click", () => {
  requestOpenCampaignHub();
});
pauseBtn?.addEventListener("click", () => {
  unlockAudio();
  togglePause();
});
document.getElementById("btn-pause-resume")?.addEventListener("click", () => {
  if (game.userPaused) togglePause();
});
document.getElementById("btn-pause-restart")?.addEventListener("click", () => {
  requestRestart();
});
document.getElementById("btn-pause-map")?.addEventListener("click", () => {
  requestOpenCampaignHub();
});
document.getElementById("btn-pause-audio")?.addEventListener("click", () => {
  const panel = document.getElementById("audio-panel");
  const btn = document.getElementById("btn-audio") as HTMLButtonElement | null;
  panel?.classList.toggle("hidden");
  const open = panel && !panel.classList.contains("hidden");
  btn?.setAttribute("aria-expanded", open ? "true" : "false");
});
document.getElementById("btn-pause-how")?.addEventListener("click", () => {
  unlockAudio();
  openHowToFightSheet();
});
difficultySelect?.addEventListener("change", () => {
  if (playMode === "free") restart();
});

// Deck library for endless free play
{
  const fromUrl = parseDeckFromUrl();
  if (fromUrl) freePlayDeckId = fromUrl;
  refreshDeckSelect();
  deckSelect?.addEventListener("change", () => {
    if (freeVariant === "muster") return;
    const id = deckSelect.value;
    if (!id || id === freePlayDeckId) return;
    if (playMode === "free" && game.status === "playing") {
      const ok = window.confirm(
        "Switch deck and restart this endless run?",
      );
      if (!ok) {
        deckSelect.value = freePlayDeckId;
        return;
      }
    }
    freePlayDeckId = id;
    setActiveDeck(id);
    refreshDeckSelect();
    if (playMode === "free") {
      restart();
    }
  });
}

window.addEventListener("keydown", (e) => {
  if (e.code !== "Space" && e.key !== " ") return;
  if (isHubShell() || isFreePlayStartOpen()) return;

  if (storyPanels?.isOpen()) return; // StoryPanels handles Space to advance
  // Teaching steps: Space dismisses (same as CTA). Sheet: Space closes sheet only.
  if (coach?.isOpen()) {
    e.preventDefault();
    coach.dismiss();
    return;
  }
  const t = e.target as HTMLElement | null;
  const tag = t?.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t?.isContentEditable) {
    return;
  }
  e.preventDefault();
  togglePause();
});

let endShown = false;

function maybeShowEnd(): void {
  if (game.status === "playing") {
    endShown = false;
    return;
  }
  if (endShown) return;
  endShown = true;
  const report = game.getRunReport();
  const payload = {
    won: game.status === "won",
    lastMessage: game.lastMessage,
    stats: report.stats,
    log: report.log,
  };

  if (playMode === "campaign" && activeRun && payload.won) {
    const mission = missionById(activeRun.missionId);
    const reward = handleCampaignVictory();
    if (mission && (reward.kind === "pick" || reward.kind === "auto" || mission.story?.outro?.length)) {
      startVictoryFlow(payload, reward, mission);
      return;
    }
  }

  // Muster: shape start deck before the end summary (no collection packs)
  if (playMode === "free" && freeVariant === "muster" && game.isEndless()) {
    showMusterEndEdit(payload);
    return;
  }

  // Open endless: verse pack / gallery keepsake before the end screen
  if (playMode === "free" && game.isEndless() && rewardPicker) {
    const pack = evaluateEndlessPack(payload.stats.elapsed);
    if (pack.kind === "cards") {
      pendingEndReport = payload;
      rewardContext = "endless-pack";
      pendingPickOptions = pack.options;
      rewardPicker.showPick(pack.options, {
        title: "Verse pack",
        lead: packReasonLead(pack),
        ownedCounts: loadCollection().ownedCounts,
        skipLabel: "Skip pack",
      });
      if (statusPill) statusPill.textContent = "Pack";
      return;
    }
    if (pack.kind === "art") {
      pendingEndReport = payload;
      const artIds = claimEndlessArtPack();
      const titles = artIds
        .map(
          (id) =>
            GALLERY_CHAPTER_PIECES.find((p) => p.id === id)?.title ?? id,
        )
        .join(" · ");
      rewardContext = "endless-art";
      rewardPicker.showNotice({
        title: "Gallery keepsake",
        lead: artIds.length
          ? `${packReasonLead(pack)} Unlocked: ${titles}.`
          : packReasonLead(pack),
        continueLabel: "Continue",
      });
      if (statusPill) statusPill.textContent = "Keepsake";
      return;
    }
  }

  const enriched = enrichEndPayload(payload);
  endScreen.show(enriched);
  if (statusPill) {
    statusPill.textContent = enriched.endless
      ? enriched.endless.isNewBest
        ? "Best!"
        : "Run over"
      : payload.won
        ? "Victory"
        : "Retreated";
  }
  syncModeChrome();
}

function syncUi(forceBoard: boolean): void {
  const snap = game.snapshot();
  hud.render(snap);
  // Keep queue badge in sync with hand instances
  if (queuedDrop && !game.hand.some((c) => c.instanceId === queuedDrop!.cardId)) {
    clearQueueUi();
  } else {
    handUi.setQueuedId(queuedDrop?.cardId ?? null);
    updateQueueBanner();
  }
  // Avoid rebuilding the hand mid-drag (destroys drag source / drops)
  if (!dragActive) {
    handUi.render(game);
  }
  syncPauseChrome();
  const vitals = {
    proclaiming: snap.proclaimLock > 0.05,
    integrity: snap.resources.integrity,
    maxIntegrity: snap.resources.maxIntegrity,
  };
  // Never rebuild the board DOM while dragging — slots under the cursor vanish
  if ((forceBoard || boardDirty) && !dragActive) {
    boardUi.render(snap.board, handUi.getDragId(), vitals);
    boardDirty = false;
  } else {
    boardUi.updateMeters(snap.board, vitals);
  }
  if (reveal.isOpen()) {
    const card = game.hand.find((c) => c.instanceId === reveal.getCurrentId()) ?? null;
    if (card) reveal.refresh(card);
  }
}

function clearQueueUi(): void {
  queuedDrop = null;
  handUi.setQueuedId(null);
  updateQueueBanner();
}

function updateQueueBanner(): void {
  if (!queueBanner || !queueRefEl) return;
  if (!queuedDrop) {
    queueBanner.classList.add("hidden");
    queueRefEl.textContent = "";
    return;
  }
  queueBanner.classList.remove("hidden");
  queueRefEl.textContent = queuedDrop.reference;
}

function flushQueuedDrop(): void {
  if (!queuedDrop) return;
  if (game.status !== "playing" || game.paused || game.isProclaiming()) return;
  const { intent, cardId } = queuedDrop;
  // Card may have left the hand
  if (!game.hand.some((c) => c.instanceId === cardId)) {
    clearQueueUi();
    handUi.invalidate();
    syncUi(false);
    return;
  }
  const preDef = game.hand.find((c) => c.instanceId === cardId)?.def;
  clearQueueUi();
  const ok = game.activate(cardId, intent);
  handUi.invalidate();
  boardDirty = true;
  syncUi(true);
  if (ok) {
    objectiveBanner?.hide();
    notePlaceablePlayed(preDef?.kind);
    scheduleFirstProclaimCoach();
    maybeShowEnd();
  }
}

queueCancelBtn?.addEventListener("click", () => {
  if (queuedDrop) {
    audio.emit({ type: "ui.queueCancel" });
  }
  clearQueueUi();
  handUi.invalidate();
  syncUi(true);
});

let last = performance.now();
let uiAccum = 0;
let presenceHash = "";

/**
 * Structure only — slot occupants + face identity.
 * Soft state (push bar, wear) updates without rebuild; morph / level / type need one.
 */
function structureHash(): string {
  let h = "";
  for (const id of ["mind", "heart", "will"] as const) {
    for (const p of game.board[id].slots) {
      h += p
        ? `${p.instanceId}:${p.defId}:${p.level}:${p.lieType ?? p.category ?? ""}`
        : ".";
      h += "|";
    }
  }
  return h;
}

function runBot(dt: number): void {
  if (!botCtrl || botId === "off") return;
  if (game.status !== "playing") return;
  if (game.userPaused) return;
  if (dragActive) return;

  // Sync UI reveal modal with bot open/close
  const hadReveal = !!game.pendingRevealId;
  const action = botCtrl.tick(game, dt);
  if (!action) return;

  if (action.type === "reveal" && game.pendingRevealId) {
    const card = game.hand.find((c) => c.instanceId === game.pendingRevealId);
    if (card) reveal.show(card);
  }
  if (hadReveal && !game.pendingRevealId && reveal.isOpen()) {
    reveal.hide();
  }

  if (action.type === "wait") return;

  handUi.invalidate();
  boardDirty = true;
  syncUi(true);
  maybeShowEnd();
}

function frame(now: number): void {
  const dt = Math.min(0.05, (now - last) / 1000);
  last = now;

  const shell = getShellMode();
  const rewardOpen = rewardPicker?.isOpen() ?? false;
  // Sim only runs in the combat shell (not under hub / story / reward pickers)
  const combatLive = shell === "combat" && !rewardOpen;
  if (combatLive) {
    // Don't advance combat under a teaching coach (userPaused already freezes update via game.paused)
    game.update(dt);
    if (audioUnlocked) {
      audio.tick(audioFrameFromProclaimSnapshot(game.snapshot()), dt);
    }
    flushQueuedDrop();
    runBot(dt);
    // First integrity loss while Will is held → staged coach
    if (game.status === "playing" && !game.userPaused) {
      maybeScheduleAbideCoach();
      maybeShowWillDrainCoach();
      maybeHandleDogmaNotice();
    }
    if (game.status === "playing") {
      lastIntegritySample = game.resources.integrity;
    }
  }

  const h = structureHash();
  if (h !== presenceHash) {
    presenceHash = h;
    if (!dragActive) boardDirty = true;
  }

  uiAccum += dt;
  if (
    uiAccum >= 0.1 ||
    game.paused ||
    game.status !== "playing" ||
    (boardDirty && !dragActive)
  ) {
    uiAccum = 0;
    if (combatLive) {
      syncUi(boardDirty && !dragActive);
      maybeShowEnd();
    }
  }
  requestAnimationFrame(frame);
}

// Boot: campaign hub (library or deep-linked stage map) or free-play endless
if (playMode === "campaign" && campaignHub) {
  openCampaignHub(parseHubBootOpts());
} else if (freeVariant === "muster") {
  enterMuster();
} else {
  // Apply endless scenario (constructor default is campaign hold scenario)
  const start = parseFreePlayStart();
  enterFreePlay({
    resume: start === "resume" ? true : undefined,
    forceNew: start === "new",
    deckId: parseDeckFromUrl(),
  });
}
requestAnimationFrame(frame);
